import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppleButton } from "@/components/ui/apple-button";
import { Input } from "@/components/ui/input";
import { Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AssetList } from "@/components/assets/AssetList";
import { EmptyAssetState } from "@/components/assets/EmptyAssetState";
import { ExtraFilters } from "@/components/assets/ExtraFilters";
import { Asset } from "@/types/assets";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

interface Company {
  id: number;
  name: string;
}

interface Place {
  id: number;
  name: string;
  company_id: number;
}

interface PaginationMetadata {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

const Assets = () => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [places, setPlaces] = useState<Place[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedPlaceId, setSelectedPlaceId] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedRating, setSelectedRating] = useState<string>("all");
  const [selectedConditions, setSelectedConditions] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata>({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasMore: false
  });
  const navigate = useNavigate();
  const { toast } = useToast();

const fetchCompanies = async () => {
    try {
  const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return;

      const { data: companiesData, error } = await supabase
    .from('companies')
        .select(`
          id,
          name
        `)
    .eq('user_id', user.id)
    .order('name');

  if (error) throw error;
      setCompanies(companiesData || []);
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      toast({
        variant: "destructive",
        title: "Error fetching companies",
        description: error.message,
      });
    }
};

  const fetchPlaces = async (companyId: string) => {
    if (!companyId) {
      setPlaces([]);
      return;
    }

    try {
      const { data: placesData, error } = await supabase
    .from('places')
    .select(`
      id,
      name,
      description,
      company_id,
      companies (
        id,
        name
      )
    `)
    .eq('company_id', parseInt(companyId))
    .order('name');

  if (error) throw error;
      setPlaces(placesData || []);
    } catch (error: any) {
      console.error('Error fetching places:', error);
      toast({
        variant: "destructive",
        title: "Error fetching places",
        description: error.message,
      });
    }
};

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) {
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "Please login to view your assets",
        });
        return;
      }

      const pageSize = 20;
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("assets")
        .select(`
          id,
          name,
          asset_name,
          asset_description,
          asset_contact,
          asset_price,
          category,
          code,
          company_id,
          conditions,
          created_at,
          documents,
          images,
          internal_asset_id,
          location,
          latitude,
          longitude,
          nfc_id,
          notes,
          phone,
          place_id,
          private,
          private_code,
          purchase_date,
          qr_id,
          rating,
          see_info,
          see_records,
          add_records,
          serial_number,
          sharing_type,
          status,
          updated_at,
          user_id,
          user_name,
          warranty_expiry,
          asset_records (
            id,
            alert,
            approval_by,
            approval_date,
            approval_status,
            asset_id,
            assigned_to,
            category,
            company_id,
            cost,
            created_at,
            details,
            documents,
            due_date,
            images,
            location,
            notes,
            phone,
            place_id,
            priority,
            progress,
            status,
            title,
            type,
            updated_at,
            user_id
          ),
          shared_assets (
            shared_with
          ),
          company:companies!company_id (
            id,
            name,
            description
          ),
          place:places!place_id (
            id,
            name,
            description
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      // Add search filter if search query exists
      if (searchQuery) {
        query = query.or(`asset_name.ilike.%${searchQuery}%,name.ilike.%${searchQuery}%`);
      }

      // Add category filter
      if (selectedCategory !== "all") {
        query = query.eq('category', selectedCategory);
      }

      // Add rating filter
      if (selectedRating !== "all") {
        query = query.eq('rating', parseInt(selectedRating));
      }

      // Add conditions filter
      if (selectedConditions !== "all") {
        query = query.eq('conditions', parseInt(selectedConditions));
      }

      // Filter by selected place if one is selected
      if (selectedPlaceId) {
        query = query.eq('place_id', parseInt(selectedPlaceId));
      }

      // Apply pagination after all filters
      query = query.range(from, to);

      const { data: assetsData, error: assetsError, count } = await query;

      if (assetsError) throw assetsError;

      // Transform the data to match our Asset type
      const transformedData = (assetsData || []).map(item => {
        const asset: Asset = {
          ...item,
          asset_records: item.asset_records || [],
          shared_assets: item.shared_assets || [],
          company: item.company && Array.isArray(item.company) && item.company[0] ? {
            id: item.company[0].id,
            name: item.company[0].name,
            description: item.company[0].description || undefined
          } : undefined,
          place: item.place && Array.isArray(item.place) && item.place[0] ? {
            id: item.place[0].id,
            name: item.place[0].name,
            description: item.place[0].description || undefined
          } : undefined,
          conditions: item.conditions || 0,
          rating: item.rating || 0,
          see_info: item.see_info || false,
          see_records: item.see_records || false,
          add_records: item.add_records || false
        };
        return asset;
      });

      setAssets(transformedData);
      setPaginationMetadata({
        currentPage,
        pageSize,
        totalCount: count || 0,
        totalPages: count ? Math.ceil(count / pageSize) : 0,
        hasMore: count ? from + pageSize < count : false
      });

    } catch (error: any) {
      console.error('Error:', error);
      toast({
        variant: "destructive",
        title: "Error fetching assets",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchPlaces(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    fetchAssets();
  }, [selectedPlaceId, currentPage, searchQuery, selectedCategory, selectedRating, selectedConditions]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextPage = () => {
    if (paginationMetadata.hasMore) {
      setCurrentPage((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Remove client-side filtering
  const filteredAssets = assets;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('assets.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('assets.description')}
          </p>
        </div>
        <AppleButton 
          onClick={() => navigate("/assets/new")}
          className="w-full sm:w-auto flex items-center justify-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('assets.newAsset')}
        </AppleButton>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          value={selectedCompanyId}
          onValueChange={setSelectedCompanyId}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('assets.selectCompany')} />
          </SelectTrigger>
          <SelectContent>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.id.toString()}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedPlaceId}
          onValueChange={setSelectedPlaceId}
          disabled={!selectedCompanyId}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('assets.selectPlace')} />
          </SelectTrigger>
          <SelectContent>
            {places.map((place) => (
              <SelectItem key={place.id} value={place.id.toString()}>
                {place.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedCategory}
          onValueChange={setSelectedCategory}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('assets.selectCategory')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {/* Equipment */}
            <SelectItem value="Heavy Equipment">Heavy Equipment</SelectItem>
            <SelectItem value="Light Equipment">Light Equipment</SelectItem>
            <SelectItem value="Medical Equipment">Medical Equipment</SelectItem>
            <SelectItem value="Office Equipment">Office Equipment</SelectItem>
            <SelectItem value="IT Equipment">IT Equipment</SelectItem>
            <SelectItem value="Laboratory Equipment">Laboratory Equipment</SelectItem>
            <SelectItem value="Safety Equipment">Safety Equipment</SelectItem>
            
            {/* Vehicles */}
            <SelectItem value="Commercial Vehicle">Commercial Vehicle</SelectItem>
            <SelectItem value="Construction Vehicle">Construction Vehicle</SelectItem>
            <SelectItem value="Emergency Vehicle">Emergency Vehicle</SelectItem>
            <SelectItem value="Fleet Vehicle">Fleet Vehicle</SelectItem>
            
            {/* Tools */}
            <SelectItem value="Hand Tools">Hand Tools</SelectItem>
            <SelectItem value="Power Tools">Power Tools</SelectItem>
            <SelectItem value="Precision Tools">Precision Tools</SelectItem>
            <SelectItem value="Measuring Tools">Measuring Tools</SelectItem>
            
            {/* Machinery */}
            <SelectItem value="Industrial Machinery">Industrial Machinery</SelectItem>
            <SelectItem value="Manufacturing Machinery">Manufacturing Machinery</SelectItem>
            <SelectItem value="Processing Machinery">Processing Machinery</SelectItem>
            
            {/* Infrastructure */}
            <SelectItem value="Building">Building</SelectItem>
            <SelectItem value="Facility">Facility</SelectItem>
            <SelectItem value="Infrastructure">Infrastructure</SelectItem>
            
            {/* Technology */}
            <SelectItem value="Software">Software</SelectItem>
            <SelectItem value="Hardware">Hardware</SelectItem>
            <SelectItem value="Network Equipment">Network Equipment</SelectItem>
            
            {/* Others */}
            <SelectItem value="Furniture">Furniture</SelectItem>
            <SelectItem value="Inventory">Inventory</SelectItem>
            <SelectItem value="Supplies">Supplies</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>

        <ExtraFilters
          rating={selectedRating}
          conditions={selectedConditions}
          onRatingChange={setSelectedRating}
          onConditionsChange={setSelectedConditions}
        />

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-apple-gray" />
          <Input
            type="search"
            placeholder={t('assets.searchAssets')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "w-full pl-9",
              "bg-white border-apple-light-gray/30",
              "placeholder:text-apple-gray",
              "focus-visible:ring-apple-blue focus-visible:ring-offset-0",
              "rounded-apple"
            )}
          />
        </div>
      </div>

      {filteredAssets.length === 0 && !loading ? (
        <EmptyAssetState />
      ) : (
        <>
          <AssetList assets={filteredAssets} onRefresh={fetchAssets} />
          
          {/* Pagination Controls */}
          {paginationMetadata && (
            <div className="mt-8 flex items-center justify-between border-t border-apple-light-gray/30 pt-4">
              <div className="text-sm text-apple-gray">
                {t('assets.showing')} {((paginationMetadata.currentPage - 1) * paginationMetadata.pageSize) + 1} {t('assets.to')}{' '}
                {Math.min(paginationMetadata.currentPage * paginationMetadata.pageSize, paginationMetadata.totalCount)} {t('assets.of')}{' '}
                {paginationMetadata.totalCount} {t('assets.assets')}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="text-apple-gray hover:text-apple-dark"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t('assets.previous')}
                </Button>
                <div className="text-sm text-apple-gray px-4">
                  {t('assets.page')} {paginationMetadata.currentPage} {t('assets.of')} {paginationMetadata.totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!paginationMetadata.hasMore}
                  className="text-apple-gray hover:text-apple-dark"
                >
                  {t('assets.next')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Assets;